import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { productZ, ProductFormValues } from '@libs/schemas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { SheetClose, SheetFooter } from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from 'components/useToast';

interface SheetProductFormProps {
  initialData?: Partial<ProductFormValues>;
  onSubmit: (data: ProductFormValues) => void;
  onToggleArchive?: (productId: string) => Promise<void>;
  isLoading?: boolean;
  productId?: string;
}

export const SheetProductForm: FC<SheetProductFormProps> = ({
  initialData,
  onSubmit,
  onToggleArchive,
  isLoading = false,
  productId,
}) => {
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const { errorToast } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ProductFormValues>({
    resolver: zodResolver(productZ),
    defaultValues: {
      label: '',
      description: '',
      priceInCents: '',
      collectTaxes: false,
      customerVisible: false,
      archived: false,
      ...initialData,
    },
  });

  const collectTaxes = watch('collectTaxes');
  const customerVisible = watch('customerVisible');
  const isArchived = watch('archived');

  const handleToggleArchive = async () => {
    if (!onToggleArchive || !productId) return;

    setIsArchiveLoading(true);
    try {
      await onToggleArchive(productId);
      // Update the form value to reflect the change
      setValue('archived', !isArchived);
      setIsArchiveDialogOpen(false);
    } catch (error) {
      errorToast({
        title: 'Failed to archive product',
        description: 'Please try again.',
      });
    } finally {
      setIsArchiveLoading(false);
    }
  };

  return (
    <div className="overflow-y-auto">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        <div className="flex-grow px-6 py-6">
          <div className="space-y-2">
            <Label className="label text-xs" htmlFor="name">
              Name:
            </Label>
            <Input
              id="label"
              placeholder="Product name"
              {...register('label')}
              className={errors.label ? 'border-red-500' : ''}
            />
            {errors.label && <p className="text-sm text-red-500">{errors.label.message}</p>}
          </div>

          <div className="space-y-2">
            <Label className="label text-xs" htmlFor="description">
              Description:
            </Label>
            <Textarea
              id="description"
              placeholder="Product description"
              {...register('description')}
              className={errors.description ? 'border-red-500' : ''}
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-red-500">{errors.description.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="label text-xs" htmlFor="priceInCents">
              Price ($):
            </Label>
            <Input
              id="priceInCents"
              type="number"
              step="0.01"
              {...register('priceInCents')}
              className={errors.priceInCents ? 'border-red-500' : ''}
            />
            {errors.priceInCents && (
              <p className="text-sm text-red-500">{errors.priceInCents.message}</p>
            )}
          </div>

          <div className="flex flex-row justify-end items-center gap-4 pt-4">
            <div className="flex items-center gap-2 justify-between">
              <div>
                <Label htmlFor="collectTaxes">Collect Taxes</Label>
              </div>
              <Switch
                id="collectTaxes"
                checked={collectTaxes}
                onCheckedChange={(checked) => setValue('collectTaxes', checked)}
              />
            </div>

            <div className="flex items-center gap-2 justify-between">
              <div>
                <Label htmlFor="customerVisible">Customer Visible</Label>
              </div>
              <Switch
                id="customerVisible"
                checked={customerVisible}
                onCheckedChange={(checked) => setValue('customerVisible', checked)}
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end w-full">
          <SheetFooter className="border-t bg-gray-50 px-6 py-4 flex w-full justify-between gap-4">
            <div className="flex flex-row gap-4 justify-between w-full">
              <Button
                variant={isArchived ? 'default' : 'destructive'}
                size="sm"
                className="w-full sm:w-auto"
                onClick={(event) => {
                  event.preventDefault();
                  setIsArchiveDialogOpen(true);
                }}
              >
                {isArchived ? 'Reactivate Product' : 'Archive Product'}
              </Button>
              <div className="flex flex-row gap-4">
                <SheetClose asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full sm:w-auto"
                    disabled={isLoading || isArchived}
                  >
                    Cancel
                  </Button>
                </SheetClose>
                <Button type="submit" size="sm" disabled={isLoading || isArchived}>
                  {isLoading ? 'Saving...' : 'Save Product'}
                </Button>
              </div>
            </div>
          </SheetFooter>
        </div>
      </form>

      <AlertDialog open={isArchiveDialogOpen} onOpenChange={setIsArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArchived ? 'Reactivate Product' : 'Archive Product'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isArchived && 'Are you sure you want to reactivate this product?'}
              {!isArchived &&
                'Are you sure you want to archive this product? It will not be accessible in invoices.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsArchiveDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleArchive} disabled={isArchiveLoading}>
              {isArchiveLoading ? 'Processing...' : isArchived ? 'Reactivate' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
